<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">Bugs</h1>
        <div class="text-lg mt-2 flex flex-col">
          <span v-if="projeto" class="font-bold">
            Projeto: <span class="font-medium">{{ projeto }}</span>
          </span>
        </div>
      </v-container>
    </div>

    <v-container>
      <div class="search-bar w-full pb-4">
        <v-text-field
          v-model="searchQuery"
          variant="solo"
          flat
          hide-details
          class="search-input"
          placeholder="Buscar bug por descrição, status, tipo, gravidade, CSU ou origem"
          aria-label="Buscar bug"
          append-inner-icon="mdi-magnify"
        ></v-text-field>
      </div>

      <!-- Menu de Filtros por Status -->
      <div class="status-filters w-full pb-4 onest-font">
        <v-chip-group
          v-model="selectedStatus"
          column
          mandatory
          selected-class="bg-primary text-white"
        >
          <v-chip value="TODOS" variant="outlined" class="font-medium">
            Todos
          </v-chip>
          <v-chip
            v-for="(label, value) in statusOptions"
            :key="value"
            :value="value"
            variant="outlined"
            class="font-medium"
          >
            {{ label }}
          </v-chip>
        </v-chip-group>
      </div>

      <div v-if="isLoading" class="py-12 text-center text-gray-500 font-medium">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <p>Carregando bugs...</p>
      </div>

      <div v-else>
        <v-row v-if="filteredBugs.length > 0">
          <v-col>
            <div class="d-flex justify-space-between align-center my-4">
              <div v-if="selectionMode" class="d-flex align-center gap-2">
                <span class="text-subtitle-2 mr-2"
                  >{{ selectedBugs.length }} selecionados</span
                >
                <v-btn
                  color="primary"
                  @click="abrirAgruparModal"
                  :disabled="selectedBugs.length < 2"
                >
                  Agrupar Bugs
                </v-btn>
                <v-btn
                  color="error"
                  variant="text"
                  @click="cancelSelectionMode"
                >
                  Cancelar
                </v-btn>
              </div>
              <div v-else class="d-flex align-center gap-2">
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-format-list-checks"
                  @click="ativarModoAgrupamento"
                >
                  Agrupar Bugs
                </v-btn>
              </div>
            </div>
            <div class="my-4">
              <table class="w-full border-separate border-spacing-y-2">
                <thead class="p-2">
                  <tr>
                    <th
                      v-if="selectionMode"
                      class="p-4 font-medium text-center w-12"
                    ></th>
                    <th class="p-4 font-medium text-left">Descrição</th>
                    <th class="p-4 font-medium text-left">Status</th>
                    <th class="p-4 font-medium text-left">Tipo</th>
                    <th class="p-4 font-medium text-left">Gravidade</th>
                    <th class="p-4 font-medium text-left">CSU Relacionado</th>
                    <th class="p-4 font-medium text-left">Origem</th>
                    <th class="p-4 font-medium text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in filteredBugs"
                    :key="item.id_bug"
                    class="hover:bg-blue-50/50 transition-colors bg-white shadow-sm"
                    @dblclick="toggleSelectionMode(item)"
                  >
                    <td
                      v-if="selectionMode"
                      class="py-4 px-4 text-center rounded-l-xl"
                    >
                      <v-checkbox
                        v-model="selectedBugs"
                        :value="item"
                        hide-details
                      ></v-checkbox>
                    </td>
                    <td
                      class="py-4 px-6 text-gray-600 max-w-[300px] truncate"
                      :class="!selectionMode ? 'rounded-l-xl' : ''"
                      :title="item.descricao"
                    >
                      {{ item.descricao }}
                    </td>

                    <td class="py-4 px-6 text-left">
                      <v-chip
                        :color="getStatusColor(item.status_bug)"
                        class="font-medium"
                        size="small"
                        variant="tonal"
                      >
                        {{ converterEnumParaTexto(item.status_bug) }}
                      </v-chip>
                    </td>

                    <td class="py-4 px-6 text-gray-600">
                      {{ converterEnumParaTexto(item.tipo_bug) }}
                    </td>

                    <td class="py-4 px-6 text-gray-600">
                      {{ converterEnumParaTexto(item.gravidade) }}
                    </td>

                    <td class="py-4 px-6 text-blue-600">
                      <router-link
                        :to="
                          '/projects/' +
                          idProjeto +
                          '/CasoUso/' +
                          item.id_caso_de_uso
                        "
                        class="text-decoration-none hover:underline"
                      >
                        {{ item.identificador || 'N/A' }}
                      </router-link>
                    </td>

                    <td class="py-4 px-6 text-left">
                      <v-chip
                        v-if="item.origem_bug"
                        :color="getOrigemColor(item.origem_bug)"
                        variant="tonal"
                        size="small"
                        class="font-medium"
                      >
                        <v-icon start size="x-small">{{
                          getOrigemIcon(item.origem_bug)
                        }}</v-icon>
                        {{ getOrigemLabel(item.origem_bug) }}
                      </v-chip>
                      <span v-else class="text-gray-500">-</span>
                    </td>

                    <td class="py-4 px-6 text-center rounded-r-xl">
                      <v-tooltip location="bottom" text="Visualizar">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            color="primary"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full"
                            @click="visualizarBug(item)"
                          >
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            class="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm mt-4"
          >
            <div class="text-6xl mb-4">🐛</div>
            <p class="text-lg">
              Nenhum bug encontrado para o filtro selecionado.
            </p>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <v-dialog v-model="showAgruparModal" max-width="600px">
      <v-card>
        <v-card-title class="text-h5 font-bold mt-2"
          >Agrupar Bugs Redundantes</v-card-title
        >
        <v-card-text>
          <p class="mb-4">
            Selecione qual bug será o <strong>Principal</strong> (a descrição
            dele será mantida). Os outros bugs selecionados serão classificados
            como redundantes e sumirão da lista.
          </p>

          <v-radio-group v-model="agruparPrincipalId">
            <v-radio
              v-for="bug in selectedBugs"
              :key="bug.id_bug"
              :value="bug.id_bug"
            >
              <template #label>
                <div>
                  <strong>{{ bug.identificador || 'N/A' }}</strong> -
                  {{ bug.descricao }}
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="showAgruparModal = false"
            >Cancelar</v-btn
          >
          <v-btn
            color="primary"
            :disabled="!agruparPrincipalId"
            :loading="agrupando"
            @click="confirmarAgrupamento"
            >Agrupar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * @module ListaBugs
 * @desc Tela que lista os bugs relacionados a um determinado projeto.
 */
import axios from 'axios';

import store from '@/store/store';

export default {
  name: 'ListaBugs',

  data() {
    return {
      idProjeto: null,
      bugs: [],
      isLoading: false,
      projeto: '',
      searchQuery: '',
      selectedStatus: 'TODOS',
      selectionMode: false,
      selectedBugs: [],
      showAgruparModal: false,
      agruparPrincipalId: null,
      agrupando: false,
      statusOptions: {
        EM_ANALISE: 'Em análise',
        PARA_FAZER: 'Para fazer',
        NEGADO: 'Negado',
        PARA_VALIDACAO: 'Para validação',
        REALIZADO: 'Realizado',
        CANCELADO: 'Cancelado',
      },
    };
  },

  computed: {
    filteredBugs() {
      let result = this.bugs || [];

      if (this.selectedStatus !== 'TODOS') {
        result = result.filter((bug) => bug.status_bug === this.selectedStatus);
      }

      if (!this.searchQuery) {
        return result;
      }
      const query = this.searchQuery.toLowerCase();

      return result.filter((bug) => {
        const descricao = bug.descricao ? bug.descricao.toLowerCase() : '';
        const csu = bug.identificador ? bug.identificador.toLowerCase() : '';
        const status = this.converterEnumParaTexto(
          bug.status_bug,
        ).toLowerCase();
        const tipo = this.converterEnumParaTexto(bug.tipo_bug).toLowerCase();
        const gravidade = this.converterEnumParaTexto(
          bug.gravidade,
        ).toLowerCase();
        const origem = this.getOrigemLabel(bug.origem_bug).toLowerCase();

        return (
          descricao.includes(query) ||
          csu.includes(query) ||
          status.includes(query) ||
          tipo.includes(query) ||
          gravidade.includes(query) ||
          origem.includes(query)
        );
      });
    },
  },

  created() {
    this.idProjeto = this.$route.params.idProjeto;
    this.projeto = localStorage.getItem('projeto') || 'Projeto Atual';
    this.getBugsFromProject();
  },

  methods: {
    toggleSelectionMode(item) {
      if (!this.selectionMode) {
        this.selectionMode = true;
        this.selectedBugs = [item];
      }
    },
    ativarModoAgrupamento() {
      this.selectionMode = true;
      this.selectedBugs = [];
    },
    cancelSelectionMode() {
      this.selectionMode = false;
      this.selectedBugs = [];
    },
    abrirAgruparModal() {
      this.agruparPrincipalId = null;
      this.showAgruparModal = true;
    },
    async confirmarAgrupamento() {
      if (!this.agruparPrincipalId) {
        return;
      }
      this.agrupando = true;
      try {
        const redundantes = this.selectedBugs
          .map((b) => b.id_bug)
          .filter((id) => id !== this.agruparPrincipalId);

        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        await axios.post(
          `${apiUrl}/api/v1/bug/agrupar-redundantes`,
          {
            // eslint-disable-next-line camelcase
            id_principal: this.agruparPrincipalId,
            // eslint-disable-next-line camelcase
            ids_redundantes: redundantes,
          },
          {
            headers: {
              Authorization: `Bearer ${
                this.$store.state.token || localStorage.getItem('token')
              }`,
            },
          },
        );
        this.showAgruparModal = false;
        this.cancelSelectionMode();
        this.getBugsFromProject();
      } catch (err) {
        console.error(err);
        alert('Erro ao agrupar bugs.');
      } finally {
        this.agrupando = false;
      }
    },
    async getBugsFromProject() {
      try {
        this.isLoading = true;
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.get(
          `${apiUrl}/api/v1/bug/projeto/${this.idProjeto}`,
          {
            headers: {
              Authorization: `Bearer ${store.state.token}`,
            },
          },
        );
        this.bugs = response.data;
      } catch (error) {
        console.error('Erro ao buscar bugs do projeto:', error);
      } finally {
        this.isLoading = false;
      }
    },

    visualizarBug(bug) {
      this.$router.push(`/projects/${this.idProjeto}/Bugs/${bug.id_bug}`);
    },

    goBack() {
      this.$router.push('/project-validation/' + this.idProjeto);
    },

    getStatusColor(status) {
      const colors = {
        EM_ANALISE: 'info',
        PARA_FAZER: 'warning',
        NEGADO: 'error',
        PARA_VALIDACAO: 'primary',
        REALIZADO: 'success',
        CANCELADO: 'grey',
      };

      return colors[status] || 'grey';
    },

    getOrigemLabel(origem) {
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'Teste de Usuário';
      }
      if (origem === 'VALIDACAO') {
        return 'Validação';
      }

      return origem || '-';
    },

    getOrigemColor(origem) {
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'deep-purple';
      }
      if (origem === 'VALIDACAO') {
        return 'teal';
      }

      return 'grey';
    },

    getOrigemIcon(origem) {
      if (origem === 'TESTES_DOS_USUARIOS') {
        return 'mdi-account-voice';
      }
      if (origem === 'VALIDACAO') {
        return 'mdi-check-decagram';
      }

      return 'mdi-information';
    },

    converterEnumParaTexto(string) {
      if (!string) {
        return '-';
      }

      const labels = {
        EM_ANALISE: 'Em análise',
        PARA_FAZER: 'Para fazer',
        NEGADO: 'Negado',
        PARA_VALIDACAO: 'Para validação',
        REALIZADO: 'Realizado',
        CANCELADO: 'Cancelado',
        CORRECAO: 'Correção',
        MELHORIA: 'Melhoria',
        BLOQUEADORA: 'Bloqueadora',
        CRITICA: 'Crítica',
        MEDIA: 'Média',
        BAIXA: 'Baixa',
        TRIVIAL: 'Trivial',
      };

      return labels[string] || string.replace(/_/g, ' ').toLowerCase();
    },
  },
};
</script>
